import ConfirmNavigation from "@/components/navigation/ConfirmNavigation";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import { cn } from "@/lib/utils";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface CMSLayoutProps {
  children: React.ReactNode;
  breadcrumbs?: {
    label: string;
    to?: string;
  }[];
  hideSubmitButton?: boolean;
  pageName: string;
  submitable?: boolean;
  formIds: string[];
  havePublishedSwitch?: boolean;
  isPublished?: boolean;
  setIsPublished?: (isPublished: boolean) => void;
  isValid?: boolean;
  isLoading?: boolean;
  className?: string;
}

const CMSLayout = ({
  children,
  pageName,
  formIds,
  havePublishedSwitch = false,
  isPublished,
  setIsPublished,
  isLoading = false,
  className,
  isValid,
  breadcrumbs,
  hideSubmitButton,
}: CMSLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string, partial = false) =>
    partial ? location.pathname.startsWith(path) : location.pathname === path;

  const { instance, instanceData } = useAuth();

  const { t } = useTranslation();

  const ids = useMemo(() => {
    return formIds && formIds.length > 0 ? formIds : [];
  }, [formIds]);

  const dirtyFormsRef = React.useRef<Set<string>>(new Set());
  const [isDirty, setIsDirty] = React.useState<boolean>(false);

  React.useEffect(() => {
    const cleanups: Array<() => void> = [];

    const markDirty = (id: string) => {
      if (!dirtyFormsRef.current.has(id)) {
        dirtyFormsRef.current.add(id);
        setIsDirty(true);
      }
    };

    const markClean = (id: string) => {
      dirtyFormsRef.current.delete(id);
      setIsDirty(dirtyFormsRef.current.size > 0);
    };

    ids.forEach((id) => {
      const formElement = document.getElementById(id);
      if (!(formElement instanceof HTMLFormElement)) return;

      formElement.addEventListener("input", () => markDirty(id));
      formElement.addEventListener("reset", () => markClean(id));
      formElement.addEventListener("submit", () => markClean(id));

      cleanups.push(() => {
        formElement.removeEventListener("input", () => markDirty(id));
        formElement.removeEventListener("reset", () => markClean(id));
        formElement.removeEventListener("submit", () => markClean(id));
      });
    });

    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [ids]);

  const handleSubmitAllForms = React.useCallback(() => {
    ids.forEach((id) => {
      const formElement = document.getElementById(id);
      if (!(formElement instanceof HTMLFormElement)) return;
      if (typeof formElement.requestSubmit === "function") {
        formElement.requestSubmit();
      } else {
        formElement.submit();
      }
    });
  }, [ids]);

  return (
    <div className="bg-[#F7FAFC]">
      <div className="max-w-screen-2xl m-auto pt-8  px-8 ">
        <div className="border-b border-slate-200 pb-2 mb-8">
          <p className="text-[#0c111d] text-[32px] font-semibold font-['Instrument Sans'] leading-loose text-start">
            {t("cmsEnteteCatalogueRaisonne")} {instance ?? "Unknow instance"}
          </p>
        </div>
        <div className="justify-start items-start gap-16 inline-flex w-full">
          <div className="flex-col justify-start items-start gap-1 inline-flex">
            <Button
              onClick={() => navigate("/cms")}
              variant={isActive("/cms") ? "default" : "not_selected"}
              className="self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex"
            >
              <p className="text-sm font-semibold font-['Inter'] leading-tight">
                {t("cmsTabAccueil")}
              </p>
            </Button>
            <Button
              onClick={() => navigate("/cms/artiste")}
              variant={isActive("/cms/artiste") ? "default" : "not_selected"}
              className="self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex"
            >
              <p className="text-sm font-semibold font-['Inter'] leading-tight">
                {t("cmsTabArtiste")}
              </p>
            </Button>
            <Button
              onClick={() => navigate("/cms/selection")}
              variant={
                isActive("/cms/selection", true) ? "default" : "not_selected"
              }
              className="self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex"
            >
              <p className="text-sm font-semibold font-['Inter'] leading-tight">
                {t("cmsTabSelection")}
              </p>
            </Button>
            <Button
              onClick={() => navigate("/cms/news")}
              variant={isActive("/cms/news") ? "default" : "not_selected"}
              className="self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex"
            >
              <p className="text-sm font-semibold font-['Inter'] leading-tight">
                {t("cmsTabNews")}
              </p>
            </Button>
            <Button
              onClick={() => navigate("/cms/expertise")}
              variant={isActive("/cms/expertise") ? "default" : "not_selected"}
              className="self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex"
            >
              <p className="text-sm font-semibold font-['Inter'] leading-tight">
                {t("cmsTabExpertise")}
              </p>
            </Button>
            <Button
              onClick={() => navigate("/cms/artwork-authentication")}
              variant={
                isActive("/cms/artwork-authentication")
                  ? "default"
                  : "not_selected"
              }
              className="self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex"
            >
              <p className="text-sm font-semibold font-['Inter'] leading-tight">
                {t("cmsTabAuthenticationForm")}
              </p>
            </Button>
            <Button
              onClick={() => navigate("/cms/about-us")}
              variant={isActive("/cms/about-us") ? "default" : "not_selected"}
              className="self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex"
            >
              <p className="text-sm font-semibold font-['Inter'] leading-tight">
                {t("cmsTabAPropos")}
              </p>
            </Button>
            <Button
              onClick={() => navigate("/cms/contact")}
              variant={isActive("/cms/contact") ? "default" : "not_selected"}
              className="self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex"
            >
              <p className="text-sm font-semibold font-['Inter'] leading-tight">
                {t("cmsTabContact")}
              </p>
            </Button>
            <Button
              onClick={() => navigate("/cms/catalogue-raisonne")}
              variant={
                isActive("/cms/catalogue-raisonne") ? "default" : "not_selected"
              }
              className="self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex"
            >
              <p className="text-sm font-semibold font-['Inter'] leading-tight">
                {t("cmsTabCatalogueRaisonne")}
              </p>
            </Button>
            <Button
              onClick={() => navigate("/cms/policies")}
              variant={isActive("/cms/policies") ? "default" : "not_selected"}
              className="self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex"
            >
              <p className="text-sm font-semibold font-['Inter'] leading-tight">
                {t("cmsTabPagesLegales")}
              </p>
            </Button>
            <Button
              onClick={() => navigate("/cms/authentification")}
              variant={
                isActive("/cms/authentification") ? "default" : "not_selected"
              }
              className="self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex"
            >
              <p className="text-sm font-semibold font-['Inter'] leading-tight">
                {t("cmsTabAuthentification")}
              </p>
            </Button>
            {instanceData?.haveEshop === true && (
              <Button
                onClick={() => navigate("/cms/e-shop")}
                variant={isActive("/cms/e-shop") ? "default" : "not_selected"}
                className="self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex"
              >
                <p className="text-sm font-semibold font-['Inter'] leading-tight">
                  {t("cmsTabEshop")}
                </p>
              </Button>
            )}
          </div>
          <div className="w-full flex-col justify-start items-start gap-8 inline-flex">
            {breadcrumbs && (
              <BreadcrumbList>
                {breadcrumbs.flatMap((breadcrumb, index) => [
                  breadcrumb.to ? (
                    <BreadcrumbItem key={breadcrumb.label}>
                      <BreadcrumbLink asChild>
                        <Link to={breadcrumb.to}>{breadcrumb.label}</Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  ) : (
                    <BreadcrumbItem key={breadcrumb.label}>
                      <BreadcrumbPage>{breadcrumb.label}</BreadcrumbPage>
                    </BreadcrumbItem>
                  ),
                  ...(index < breadcrumbs.length - 1
                    ? [<BreadcrumbSeparator key={`sep-${index}`} />]
                    : []),
                ])}
              </BreadcrumbList>
            )}
            <div className="w-full pb-5 border-b border-slate-200 justify-between items-center inline-flex">
              <div className="justify-start items-center gap-4 flex">
                <p className="text-[#0c111d] text-lg font-semibold font-['Raleway'] leading-normal">
                  {pageName}
                </p>
              </div>
              <div className="justify-start items-center gap-4 flex">
                {havePublishedSwitch && (
                  <div className="items-center flex gap-1">
                    <Switch
                      checked={isPublished}
                      onCheckedChange={() =>
                        setIsPublished ? setIsPublished(!isPublished) : null
                      }
                    />
                    <Label className="text-slate-700 text-sm font-medium font-['Raleway'] leading-normal">
                      {t("cmsPublierPageLabel")}
                    </Label>
                  </div>
                )}
                {hideSubmitButton !== true && (
                  <Button
                    type="button"
                    onClick={handleSubmitAllForms}
                    variant="default"
                    className="text-white text-sm font-semibold font-['Raleway'] leading-normal"
                    disabled={!(isValid ?? true)}
                  >
                    {t("cmsEnregistrerBouton")}
                  </Button>
                )}
              </div>
            </div>
            <div
              className={cn(
                "justify-start items-start gap-8 inline-flex w-full pb-10",
                className,
              )}
            >
              {isLoading ? <Skeleton className="w-full h-full" /> : children}
            </div>
          </div>
        </div>
      </div>
      <ConfirmNavigation
        isBlocked={isDirty}
        title={t("cms.confirmNavigation.title")}
      />
    </div>
  );
};

export default CMSLayout;
